//CouponViewDialog.vue
<template lang="pug">
v-dialog(
  v-model="dialog"
  max-width="700"
)
  v-card
    v-card-title.primary--text
      | Detalles del Cupón
      v-spacer
      v-chip(:color="getCouponTypeColor(coupon.type)" small)
        | {{ getCouponTypeLabel(coupon.type) }}
    
    v-card-text
      v-row
        v-col(cols="12")
          v-card(ref="couponCard" class="coupon-card" :class="getStatusClass")
            v-card-title.d-flex.justify-space-between
              span.coupon-code {{ coupon.code }}
              .discount-badge(:class="getDiscountColor")
                span.text-h5.font-weight-bold
                  template(v-if="coupon.discountType === 'PERCENTAGE'") {{ coupon.discountValue }}%
                  template(v-else-if="coupon.discountType === 'FIXED_AMOUNT'") ${{ coupon.discountValue }}
                  template(v-else) {{ getDiscountTypeLabel(coupon.discountType) }}
                span.text-caption.d-block OFF
            v-card-text
              // Contenido completo a exportar
              div(ref="couponExport")
                .coupon-description {{ coupon.description || coupon.title }}
                .coupon-validity.d-flex.align-center.mt-2
                  v-icon(small color="grey" class="mr-1") mdi-calendar-range
                  span.text-caption Válido hasta {{ formatDate(coupon.expiresAt) }}
                div.mt-4.d-flex.align-center
                  v-img(
                    v-if="qrCodeUrl"
                    :src="qrCodeUrl"
                    alt="Código QR"
                    max-width="140"
                    max-height="140"
                    contain
                    class="elevation-2 rounded"
                    :eager="true"
                    crossorigin="anonymous"
                    @load="onQrLoaded"
                  )
                    template(v-slot:placeholder)
                      v-row.fill-height.ma-0(align='center' justify='center')
                        v-progress-circular(indeterminate color='primary')

      v-expansion-panels.mt-4(flat)
        v-expansion-panel
          v-expansion-panel-header
            .font-weight-medium Información General
          v-expansion-panel-content
            v-list(dense)
              v-list-item
                v-list-item-title Título:
                v-list-item-subtitle {{ coupon.title }}
              v-list-item
                v-list-item-title Estado:
                v-list-item-subtitle
                  v-chip(
                    x-small
                    :color="coupon.active ? 'success' : 'grey'"
                    text-color="white"
                  ) {{ coupon.active ? 'Activo' : 'Inactivo' }}
              v-list-item
                v-list-item-title Tipo de cupón:
                v-list-item-subtitle {{ getCouponTypeLabel(coupon.type) }}
              v-list-item
                v-list-item-title Descuento:
                v-list-item-subtitle
                  template(v-if="coupon.discountType === 'PERCENTAGE'") {{ coupon.discountValue }}% de descuento
                  template(v-else-if="coupon.discountType === 'FIXED_AMOUNT'") ${{ coupon.discountValue }} de descuento
                  template(v-else) {{ getDiscountTypeLabel(coupon.discountType) }}
              v-list-item(v-if="coupon.minOrderAmount > 0")
                v-list-item-title Monto mínimo:
                v-list-item-subtitle ${{ coupon.minOrderAmount }}
        
        v-expansion-panel
          v-expansion-panel-header
            .font-weight-medium Condiciones de Uso
          v-expansion-panel-content
            v-list(dense)
              v-list-item
                v-list-item-title Periodo de validez:
                v-list-item-subtitle {{ getValidityPeriod }}
              v-list-item(v-if="coupon.validDays && coupon.validDays.length")
                v-list-item-title Días válidos:
                v-list-item-subtitle {{ formatValidDays(coupon.validDays) }}
              v-list-item(v-if="coupon.validHours && (coupon.validHours.start || coupon.validHours.end)")
                v-list-item-title Horario válido:
                v-list-item-subtitle {{ formatValidHours(coupon.validHours) }}
              v-list-item
                v-list-item-title Límite de usos:
                v-list-item-subtitle {{ coupon.usedCount }} / {{ coupon.maxUses || '∞' }}
              v-list-item(v-if="coupon.maxUsesPerCustomer")
                v-list-item-title Usos por cliente:
                v-list-item-subtitle {{ coupon.maxUsesPerCustomer }}
        
        v-expansion-panel
          v-expansion-panel-header
            .font-weight-medium Restricciones
          v-expansion-panel-content
            v-list(dense)
              v-list-item(v-if="coupon.customerSegment && coupon.customerSegment !== 'ALL'")
                v-list-item-title Segmento de clientes:
                v-list-item-subtitle {{ getCustomerSegmentLabel(coupon.customerSegment) }}
              v-list-item(v-if="coupon.customerAssigned")
                v-list-item-title Cliente asignado:
                v-list-item-subtitle {{ getCustomerName(coupon.customerAssigned) || coupon.customerAssigned }}
              v-list-item(v-if="coupon.validChannels && coupon.validChannels.length")
                v-list-item-title Canales válidos:
                v-list-item-subtitle {{ formatValidChannels(coupon.validChannels) }}
              v-list-item(v-if="coupon.validCategories && coupon.validCategories.length")
                v-list-item-title Categorías válidas:
                v-list-item-subtitle {{ formatCategories(coupon.validCategories) }}
              v-list-item
                v-list-item-title Combinable con otras promociones:
                v-list-item-subtitle {{ coupon.combinable ? 'Sí' : 'No' }}
        
        v-expansion-panel
          v-expansion-panel-header
            .font-weight-medium Información adicional
          v-expansion-panel-content
            v-list(dense)
              v-list-item
                v-list-item-title Creado:
                v-list-item-subtitle {{ formatDateTime(coupon.createdAt) }}
              v-list-item
                v-list-item-title Actualizado:
                v-list-item-subtitle {{ formatDateTime(coupon.updatedAt) }}
              v-list-item
                v-list-item-title Generado por:
                v-list-item-subtitle {{ getGeneratedByLabel(coupon.generatedBy) }}
              v-list-item(v-if="coupon.relatedEntity && coupon.relatedEntity.entityType")
                v-list-item-title Entidad relacionada:
                v-list-item-subtitle {{ `${coupon.relatedEntity.entityType}: ${coupon.relatedEntity.entityId}` }}
    
    v-divider
    
    v-card-actions
      v-spacer
      v-btn(color="primary" text @click="downloadCoupon")
        v-icon(left) mdi-download
        | Descargar cupón
      v-btn(
        color="primary" 
        text
        @click="generateShareLink"
      ) 
        v-icon(left) mdi-share-variant
        | Compartir
      v-btn(text @click="close") Cerrar
    
    // Snackbar para mostrar el enlace copiado
    v-snackbar(
      v-model="showShareSnackbar"
      timeout="3000"
      color="success"
    )
      | Enlace copiado al portapapeles
      template(v-slot:action="{ attrs }")
        v-btn(
          text
          v-bind="attrs"
          @click="showShareSnackbar = false"
        ) Cerrar
</template>

<script>
import { mapState } from 'vuex'
import html2canvas from 'html2canvas'
import QRCode from 'qrcode'

export default {
  name: 'CouponViewDialog',
  
  props: {
    value: Boolean,
    coupon: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      dialog: this.value,
      showShareSnackbar: false,
      customers: [], // Se cargará con los clientes disponibles
      categories: [], // Se cargará con las categorías disponibles
      qrCodeUrl: null,
      qrLoaded: false
    }
  },
  
  computed: {
    // Determinar clase de estado (activo, vencido, inactivo)
    getStatusClass() {
      if (!this.coupon.active) return 'inactive-coupon'
      if (new Date(this.coupon.expiresAt) < new Date()) return 'expired-coupon'
      return 'active-coupon'
    },
    
    // Color del descuento según tipo
    getDiscountColor() {
      const colors = {
        'PERCENTAGE': 'primary-badge',
        'FIXED_AMOUNT': 'success-badge',
        'FREE_SHIPPING': 'purple-badge',
        'FREE_ITEM': 'orange-badge'
      }
      return colors[this.coupon.discountType] || 'grey-badge'
    },
    
    // Período de validez formateado
    getValidityPeriod() {
      const start = this.coupon.startDate 
        ? this.formatDate(this.coupon.startDate) 
        : 'Desde creación'
      const end = this.formatDate(this.coupon.expiresAt)
      
      return `${start} al ${end}`
    },
    qrTargetUrl() {
      return this.coupon && this.coupon.code ? `${window.location.origin}/promo/${this.coupon.code}` : ''
    }
  },
  
  watch: {
    value(val) {
      this.dialog = val
    },
    dialog(val) {
      if (!val) this.$emit('input', val)
    }
  },
  
  created() {
    this.generateQrCode()
  },
  
  methods: {
    generateQrCode() {
      try {
        if (!this.qrTargetUrl) { this.qrCodeUrl = null; return }
        this.qrLoaded = false
        // Generar QR como data URL para evitar CORS
        QRCode.toDataURL(this.qrTargetUrl, {
          width: 300,
          margin: 1,
          errorCorrectionLevel: 'H',
          color: { dark: '#000000', light: '#FFFFFF' }
        }).then((url) => {
          this.qrCodeUrl = url
          this.qrLoaded = true
        }).catch((e) => {
          console.error('Error generando QR local:', e)
          // Fallback a servicio externo (puede fallar CORS)
          const enc = encodeURIComponent(this.qrTargetUrl)
          this.qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${enc}&color=000000&bgcolor=FFFFFF&margin=10&format=png&ecc=H`
          this.qrLoaded = true
        })
      } catch (e) {
        console.error('Error generando QR:', e)
        this.qrCodeUrl = null
      }
    },
    onQrLoaded() {
      this.qrLoaded = true
    },
    // Cerrar el diálogo
    close() {
      this.dialog = false
    },
    
    // Generar link para compartir y copiarlo al portapapeles
    generateShareLink() {
      // Ejemplo básico: solo generamos un "enlace" con el código
      const shareText = `¡Usá el cupón ${this.coupon.code} para obtener `
        + (this.coupon.discountType === 'PERCENTAGE' 
            ? `${this.coupon.discountValue}% de descuento!` 
            : `$${this.coupon.discountValue} de descuento!`)
      
      // Copiar al portapapeles
      navigator.clipboard.writeText(shareText)
        .then(() => {
          this.showShareSnackbar = true
        })
        .catch(err => {
          console.error('Error al copiar: ', err)
        })
    },
    
    // Formateador de fechas
    formatDate(dateString) {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date)
    },
    
    // Formateador de fecha y hora
    formatDateTime(dateString) {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    
    // Etiqueta de tipo de cupón
    getCouponTypeLabel(type) {
      const labels = {
        'STANDARD': 'Estándar',
        'FIRST_ORDER': 'Primera compra',
        'LOYALTY': 'Fidelidad',
        'BIRTHDAY': 'Cumpleaños',
        'REFERRAL': 'Referido',
        'RECOVERY': 'Recuperación',
        'COMPENSATION': 'Compensación',
        'SEASONAL': 'Temporada'
      }
      return labels[type] || type
    },
    
    // Color según tipo de cupón
    getCouponTypeColor(type) {
      const colors = {
        'STANDARD': 'grey',
        'FIRST_ORDER': 'blue',
        'LOYALTY': 'deep-purple',
        'BIRTHDAY': 'pink',
        'REFERRAL': 'teal',
        'RECOVERY': 'amber',
        'COMPENSATION': 'red',
        'SEASONAL': 'green'
      }
      return colors[type] || 'grey'
    },
    
    // Etiqueta de tipo de descuento
    getDiscountTypeLabel(type) {
      const labels = {
        'PERCENTAGE': 'Porcentaje',
        'FIXED_AMOUNT': 'Monto fijo',
        'FREE_SHIPPING': 'Envío gratis',
        'FREE_ITEM': 'Item gratis'
      }
      return labels[type] || type
    },
    
    // Etiqueta de segmento de cliente
    getCustomerSegmentLabel(segment) {
      const labels = {
        'ALL': 'Todos los clientes',
        'NEW': 'Clientes nuevos',
        'INACTIVE': 'Clientes inactivos',
        'VIP': 'Clientes VIP'
      }
      return labels[segment] || segment
    },
    
    // Formatear días válidos
    formatValidDays(days) {
      if (!days || !days.length) return 'Todos los días'
      
      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
      return days.map(day => dayNames[day]).join(', ')
    },
    
    // Formatear horas válidas
    formatValidHours(hours) {
      if (!hours) return 'Todo horario'
      
      const start = hours.start || '00:00'
      const end = hours.end || '23:59'
      
      return `${start} - ${end} hs.`
    },
    
    // Formatear canales válidos
    formatValidChannels(channels) {
      if (!channels || !channels.length) return 'Todos los canales'
      
      const channelMap = {
        'DELIVERY': 'Delivery',
        'PICKUP': 'Retiro en local',
        'DINE_IN': 'Consumo en local'
      }
      
      return channels.map(c => channelMap[c] || c).join(', ')
    },
    
    // Formatear categorías válidas
    formatCategories(categoryIds) {
      if (!categoryIds || !categoryIds.length) return 'Todas las categorías'
      
      // Esto sería más útil si tuviéramos la lista completa de categorías cargada
      return categoryIds.join(', ')
    },
    
    // Obtener nombre de cliente
    getCustomerName(customerId) {
      // Esto sería más útil si tuviéramos la lista de clientes cargada
      return customerId
    },
    
    // Generado por
    getGeneratedByLabel(generatedBy) {
      const labels = {
        'MANUAL': 'Generado manualmente',
        'AUTOMATIC': 'Generado automáticamente',
        'API': 'Generado via API'
      }
      return labels[generatedBy] || generatedBy
    },
    async downloadCoupon() {
      try {
        // Esperar a que el QR esté cargado si aplica
        let attempts = 0
        while (!this.qrLoaded && attempts < 20) {
          await new Promise(r => setTimeout(r, 100))
          attempts++
        }
        await this.$nextTick()
        const el = (this.$refs.couponCard && this.$refs.couponCard.$el) ? this.$refs.couponCard.$el : (this.$refs.couponExport || null)
        if (!el || !document.body.contains(el)) {
          // intentar adjuntar temporalmente si no está en el DOM visible
          console.warn('Elemento del cupón no adjunto al documento')
          return
        }
        const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#FFFFFF', useCORS: true, logging: false, windowWidth: document.documentElement.clientWidth })
        const link = document.createElement('a')
        link.download = `cupon-${this.coupon.code}.png`
        link.href = canvas.toDataURL('image/png')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        console.error(e)
      }
    }
  }
}
</script>

<style scoped>
.coupon-card {
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
}

.active-coupon {
  border-left: 5px solid #4caf50;
}

.expired-coupon {
  border-left: 5px solid #ff9800;
  opacity: 0.8;
}

.inactive-coupon {
  border-left: 5px solid #9e9e9e;
  opacity: 0.7;
}

.coupon-code {
  font-family: monospace;
  font-size: 1.2rem;
  font-weight: bold;
  letter-spacing: 1px;
}

.discount-badge {
  padding: 8px 12px;
  border-radius: 4px;
  text-align: center;
}

.primary-badge {
  background-color: #1976d2;
  color: white;
}

.success-badge {
  background-color: #4caf50;
  color: white;
}

.purple-badge {
  background-color: #9c27b0;
  color: white;
}

.orange-badge {
  background-color: #ff9800;
  color: white;
}

.grey-badge {
  background-color: #9e9e9e;
  color: white;
}
</style>
